<template>
  <NuxtLayout name="auth">
    <div class="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand/10 blur-[120px] rounded-full"
      />
      <div
        class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand/5 blur-[120px] rounded-full"
      />
    </div>

    <div class="relative z-10 w-full max-w-lg">
      <div class="flex flex-col items-center mb-10">
        <PebbleAppHomeLogo class="h-10 mb-6" />
        <h1 class="text-4xl font-black text-white tracking-tighter mb-2">
          Welcome <span class="text-brand">Back</span>
        </h1>
        <p class="text-text-weak-dark text-xs font-bold tracking-[0.2em]">
          Good to see you again
        </p>
      </div>

      <div
        class="glass-heavy border border-white/10 rounded-[2.5rem] p-8 lg:p-12 shadow-2xl relative overflow-hidden"
      >
        <div
          class="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent"
        />
        <div class="flex justify-center items-center mb-4">
          <div
            class="px-4 bg-[#0f0f11] text-[11px] font-bold text-white/60 tracking-[0.3em]"
          >
            Continue with
          </div>
        </div>

        <PebbleAppSocialAuthentication />

        <div class="relative flex items-center justify-center mb-10">
          <span class="w-full border-t border-white/5" />
          <span
            class="absolute px-4 bg-[#0f0f11] text-[10px] font-black text-white/30 tracking-[0.3em]"
          >
            or sign in with email or phone
          </span>
        </div>

        <div class="mb-8">
          <PebbleAppTabs v-model:active-tab="activeTab" :tabs="tabs" />
        </div>

        <form
          v-if="activeTab === 'email'"
          @submit.prevent="onSubmitEmailLogin"
          class="space-y-6"
        >
          <PebbleAppFormInput
            v-model="emailValue"
            label="Email address"
            placeholder="e.g. user@pebble.com"
            type="email"
            :error="emailError"
            :disabled="isLoading"
          >
            <template #leftIcon><Mail class="w-5 h-5" /></template>
          </PebbleAppFormInput>

          <div class="space-y-2">
            <PebbleAppFormInput
              v-model="emailPasswordValue"
              label="Password"
              placeholder="••••••••"
              is-password-field
              :error="emailPasswordError"
              :disabled="isLoading"
            >
              <template #leftIcon><Lock class="w-5 h-5" /></template>
            </PebbleAppFormInput>
            <div class="text-right">
              <NuxtLink
                to="/forgot-password"
                class="text-[10px] text-brand hover:text-orange-400 font-black tracking-widest transition-colors"
              >
                Forgot password?
              </NuxtLink>
            </div>
          </div>

          <PebbleAppLink
            type="submit"
            variant="primary"
            size="lg"
            :leading-icon="LogIn"
            :full-width="true"
            :disabled="isLoading"
            :loading="isLoading"
          >
            {{ isLoading ? "Signing In..." : "Sign In" }}
          </PebbleAppLink>
        </form>

        <form
          v-if="activeTab === 'mobile'"
          @submit.prevent="onSubmitMobileLogin"
          class="space-y-6"
        >
          <PebbleAppFormInput
            v-model="mobileValue"
            v-model:country-code="selectedCountryCode"
            label="Phone number"
            placeholder="000 000 000"
            is-mobile-field
            :error="mobileError"
            :disabled="isLoading"
          />

          <div class="space-y-2">
            <PebbleAppFormInput
              v-model="mobilePasswordValue"
              label="Password"
              placeholder="••••••••"
              is-password-field
              :error="mobilePasswordError"
              :disabled="isLoading"
            >
              <template #leftIcon><Lock class="w-5 h-5" /></template>
            </PebbleAppFormInput>
            <div class="text-right">
              <NuxtLink
                to="/forgot-password"
                class="text-[10px] text-brand hover:text-orange-400 font-black tracking-widest transition-colors"
              >
                Forgot password?
              </NuxtLink>
            </div>
          </div>

          <PebbleAppLink
            type="submit"
            variant="primary"
            size="lg"
            :leading-icon="LogIn"
            :full-width="true"
            :disabled="isLoading"
            :loading="isLoading"
          >
            {{ isLoading ? "Signing In..." : "Sign In" }}
          </PebbleAppLink>
        </form>
      </div>

      <p
        class="mt-10 text-center text-xs font-bold text-text-weak-dark tracking-widest"
      >
        New here?
        <NuxtLink
          to="/get-started/viewer"
          class="text-brand hover:text-orange-400 transition-colors ml-2"
        >
          Create account
        </NuxtLink>
      </p>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useForm, useField } from "vee-validate";
import { LogIn, Mail, Lock } from "lucide-vue-next";
import { useLoginStore } from "@@/stores/auth/login";
import { emailLoginSchema, mobileLoginSchema } from "~/utils/validations/login";


usePageTitle({
  title: "Login",
  description: "Login to your Pebble account",
});

const tabs = [
  { id: "email", label: "Email" },
  { id: "mobile", label: "Mobile" },
];

const activeTab = ref<string>("email");
const selectedCountryCode = ref("+233");

const loginStore = useLoginStore();
const { handleEmailLoginSubmit, handleMobileLoginSubmit } = loginStore;
const { isLoading } = storeToRefs(loginStore);

const { handleSubmit: handleSubmitEmail } = useForm({
  validationSchema: emailLoginSchema(),
});

const { value: emailValue, errorMessage: emailError } =
  useField<string>("email");
const { value: emailPasswordValue, errorMessage: emailPasswordError } =
  useField<string>("email_password");

const onSubmitEmailLogin = handleSubmitEmail(async (values) => {
  await handleEmailLoginSubmit({
    email: values.email,
    password: values.email_password,
  });
});

const { handleSubmit: handleSubmitMobile } = useForm({
  validationSchema: mobileLoginSchema(selectedCountryCode),
});

const { value: mobileValue, errorMessage: mobileError } =
  useField<string>("mobile");
const { value: mobilePasswordValue, errorMessage: mobilePasswordError } =
  useField<string>("mobile_password");

watch(mobileValue, (newVal) => {
  if (newVal?.startsWith("0")) {
    mobileValue.value = newVal.substring(1);
  }
});

const onSubmitMobileLogin = handleSubmitMobile(async (values) => {
  const formattedMobile = `${selectedCountryCode.value}${values.mobile.replace(/^0+/, "")}`;

  await handleMobileLoginSubmit({
    mobile: formattedMobile,
    password: values.mobile_password,
  });
});
</script>

<style scoped>
.glass-heavy {
  background: rgba(15, 15, 17, 0.8);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
}
</style>
